import useStartNewBrainSearch from "@/admin/integrations/chat-bot/util/useStartNewBrainSearch"
import { ContentSuggestedPromptListItemQuestion$key } from "@/content/suggested-prompts/__generated__/ContentSuggestedPromptListItemQuestion.graphql"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import RelayEnvironment from "@/relay/RelayEnvironment"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoIcon, DiscoText, DiscoTextButton } from "@disco-ui"
import Skeleton from "@material-ui/lab/Skeleton"
import { memo, useCallback } from "react"
import { commitLocalUpdate, graphql, useFragment } from "react-relay"

interface Props {
  questionKey: ContentSuggestedPromptListItemQuestion$key
  contentUsageId: string
}

function ContentSuggestedPromptListItem({ questionKey, contentUsageId }: Props) {
  const question = useFragment(
    graphql`
      fragment ContentSuggestedPromptListItemQuestion on BrainSearchRelatedQuestion {
        id
        text
        viewerBrainSearch {
          id
        }
        viewerBrainSearchMessage {
          id
          brainSearchId
        }
      }
    `,
    questionKey
  )

  const drawer = useGlobalDrawer("brainSearch")

  const [startNewBrainSearchForm, startNewBrainSearch] = useStartNewBrainSearch({
    isSidePanel: true,
    contentUsageId,
    onCompleted: (brainSearchId: string) => {
      // Update the store directly to avoid component remounting
      commitLocalUpdate(RelayEnvironment, (store) => {
        const questionRecord = store.get(question.id)
        if (questionRecord) {
          const brainSearchRecord =
            store.get(brainSearchId) || store.create(brainSearchId, "BrainSearch")
          questionRecord.setLinkedRecord(brainSearchRecord, "viewerBrainSearch")
        }
      })
    },
  })

  const classes = useStyles()

  const handleQuestionClick = useCallback(async () => {
    // Check if brain search drawer is open with an active conversation
    if (question.viewerBrainSearch) {
      // Open existing conversation for this question
      drawer.open({
        drawerAsk: "1",
        convId: question.viewerBrainSearch.id,
      })
    } else if (question.viewerBrainSearchMessage) {
      // Open conversation containing the message created from this question
      drawer.open({
        drawerAsk: "1",
        convId: question.viewerBrainSearchMessage.brainSearchId,
        scrollToMessageId: question.viewerBrainSearchMessage.id,
      })
    } else if (drawer.params.drawerAsk && drawer.params.convId) {
      // Send message to current conversation
      drawer.setParams({
        prompt: encodeURIComponent(question.text),
        relatedQuestionId: question.id,
      })
    } else {
      // Create new conversation
      startNewBrainSearchForm.state.title = question.text
      startNewBrainSearchForm.state.brainSearchRelatedQuestionId = question.id
      await startNewBrainSearch()
    }
  }, [
    drawer,
    question.text,
    question.id,
    question.viewerBrainSearch,
    question.viewerBrainSearchMessage,
    startNewBrainSearchForm,
    startNewBrainSearch,
  ])

  const isUsed = Boolean(question.viewerBrainSearch || question.viewerBrainSearchMessage)

  return (
    <DiscoTextButton
      key={question.id}
      onClick={handleQuestionClick}
      variant={isUsed ? "grey" : undefined}
      className={classes.questionItem}
    >
      <DiscoText
        variant={"body-sm"}
        color={isUsed ? "groovy.neutral.400" : undefined}
        className={classes.questionText}
      >
        {question.text}
      </DiscoText>
      <span className={classes.arrowIcon}>
        <DiscoIcon icon={"arrow-stem-right"} />
      </span>
    </DiscoTextButton>
  )
}

function ContentSuggestedPromptListItemSkeleton() {
  const classes = useStyles()
  return (
    <div className={classes.questionItem}>
      <Skeleton width={"100%"} height={20} />
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  questionItem: {
    position: "relative",
    width: "100%",
    justifyContent: "flex-start",
    textAlign: "left",
    padding: theme.spacing(1.5),
    paddingRight: theme.spacing(5),
    borderRadius: theme.spacing(1),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& .MuiButton-label": {
      display: "flex",
      alignItems: "center",
      width: "100%",
      gap: theme.spacing(1),
    },
  },
  questionText: {
    flex: 1,
    textAlign: "left",
    minWidth: 0,
    wordBreak: "break-word",
  },
  arrowIcon: {
    display: "none",
    alignItems: "center",
    position: "absolute",
    right: theme.spacing(1.5),
    top: "50%",
    transform: "translateY(-50%)",
    "$questionItem:hover &": {
      display: "inline-flex",
    },
  },
}))

export default Relay.withSkeleton({
  component: memo(ContentSuggestedPromptListItem),
  skeleton: ContentSuggestedPromptListItemSkeleton,
})
